import React, { useState, useRef } from 'react';
import { Check, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Broker } from '../types';
import { parseZerodhaCSV, isValidCSV, ParseResult, RejectedRow } from '../utils/brokerParsers/zerodhaParser';
import { parseGrowwExcel, isValidGrowwFile } from '../utils/brokerParsers/growwParser';
import { parseWithConfig } from '../utils/brokerParsers/genericParser';
import { getParserConfig, hasParserConfig } from '../utils/brokerParsers/configs';
import { resolveSecurities, checkDuplicateOrderIds } from '../services/api';
import { analytics } from '../lib/analytics';
import ExistingPositionsScreen from './import/ExistingPositionsScreen';
import NewPositionsScreen from './import/NewPositionsScreen';

// Map display names to Broker enum
const brokerNameToEnum: Record<string, Broker> = {
  'Zerodha': Broker.ZERODHA,
  'Groww': Broker.GROWW,
  'Upstox': Broker.UPSTOX,
  'Angel One': Broker.ANGELONE,
  'ICICI Direct': Broker.ICICI_DIRECT,
  'HDFC Securities': Broker.HDFC_SECURITIES,
  'Kotak Securities': Broker.KOTAK,
};

interface ImportDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  portfolioId: string;
  portfolioBrokers: string[]; // Array of broker names from portfolio
  onImportComplete: () => void;
}

type ImportStep = 'broker-selection' | 'existing-positions' | 'new-positions';

/**
 * Resolved security data from API
 */
interface ResolvedSecurity {
  isin: string;
  ticker: string;
  exchange: 'NSE' | 'BSE';
  bse_scrip_code?: string;
  name?: string;
}

/**
 * Parsed position with resolved security data
 */
export interface ResolvedParsedPosition {
  isin: string;
  ticker: string;
  exchange: 'NSE' | 'BSE';
  bse_scrip_code?: string;
  name?: string;
  tranches: Array<{
    orderId: string;
    symbol: string;
    isin: string;
    type: 'buy' | 'sell';
    quantity: number;
    avgPrice: number;
    timestamp: string;
    exchange: string;
    isAlreadyImported: boolean;  // Flag from check-duplicates API
  }>;
}

/**
 * Warning data for unresolved/rejected securities
 */
interface ImportWarnings {
  rejectedRows: RejectedRow[];
  unresolvedIsins: string[];
}

const ImportDataModal: React.FC<ImportDataModalProps> = ({
  isOpen,
  onClose,
  portfolioId,
  portfolioBrokers,
  onImportComplete,
}) => {
  const [step, setStep] = useState<ImportStep>('broker-selection');
  const [selectedBroker, setSelectedBroker] = useState<string | null>(null);
  const [parsedData, setParsedData] = useState<ResolvedParsedPosition[]>([]);
  const [existingPositionsData, setExistingPositionsData] = useState<any[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [importWarnings, setImportWarnings] = useState<ImportWarnings | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Broker logo/icon mapping - order matters for display
  const brokerInfo: Record<string, { name: string; logo: string; enabled: boolean; fileType: string }> = {
    Zerodha: { name: 'Zerodha', logo: '🟢', enabled: true, fileType: 'CSV' },
    Groww: { name: 'Groww', logo: '🟡', enabled: true, fileType: 'Excel' },
    'ICICI Direct': { name: 'ICICI Direct', logo: '🟠', enabled: true, fileType: 'Excel' },
    Upstox: { name: 'Upstox', logo: '🔵', enabled: false, fileType: 'CSV' },
    'Angel One': { name: 'Angel One', logo: '🔴', enabled: false, fileType: 'CSV' },
    'HDFC Securities': { name: 'HDFC Securities', logo: '🟣', enabled: false, fileType: 'CSV' },
    'Kotak Securities': { name: 'Kotak Securities', logo: '⚫', enabled: false, fileType: 'CSV' },
  };

  const handleBrokerSelect = (broker: string) => {
    if (!brokerInfo[broker].enabled) return;
    
    setSelectedBroker(broker);
    setUploadError(null);
    setImportWarnings(null);
    
    // Track import started
    analytics.trackImportStarted(broker, portfolioId);
    
    // Trigger file input
    setTimeout(() => {
      fileInputRef.current?.click();
    }, 100);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadError(null);
    setImportWarnings(null);

    try {
      // Check if this broker uses the generic parser (ISIN lookup flow)
      const brokerEnum = brokerNameToEnum[selectedBroker || ''];
      const usesGenericParser = brokerEnum && hasParserConfig(brokerEnum);
      
      let parseResult: ParseResult & { unresolvedSymbols?: string[] } = { positions: [], rejectedRows: [] };
      let allIsins: string[] = [];
      
      if (usesGenericParser) {
        // Use generic parser - handles ISIN lookup internally
        const config = getParserConfig(brokerEnum);
        const genericResult = await parseWithConfig(file, config);
        parseResult = {
          positions: genericResult.positions,
          rejectedRows: genericResult.rejectedRows,
          unresolvedSymbols: genericResult.unresolvedSymbols,
        };
        allIsins = Array.from(new Set(genericResult.positions.map(p => p.isin)));
      } else if (selectedBroker === 'Zerodha') {
        // Validate CSV file
        if (!isValidCSV(file)) {
          throw new Error('Please upload a valid CSV file for Zerodha');
        }
        parseResult = await parseZerodhaCSV(file);
        allIsins = Array.from(new Set(parseResult.positions.map(p => p.isin)));
      } else if (selectedBroker === 'Groww') {
        // Validate Excel file
        if (!isValidGrowwFile(file)) {
          throw new Error('Please upload a valid Excel file (.xlsx or .xls) for Groww');
        }
        parseResult = await parseGrowwExcel(file);
        allIsins = Array.from(new Set(parseResult.positions.map(p => p.isin)));
      }

      if (parseResult.positions.length === 0 && parseResult.rejectedRows.length === 0) {
        throw new Error('No valid trades found in the file');
      }

      // Resolve ISINs via API (to get ticker, name, etc. for price lookup)
      console.log(`Resolving ${allIsins.length} ISINs...`);
      const resolveResponse = await resolveSecurities(allIsins, []);
      
      if (!resolveResponse.success) {
        throw new Error('Failed to resolve securities. Please try again.');
      }

      const { resolved, unresolved } = resolveResponse.data;
      
      // Create a map of ISIN -> resolved data (prefer NSE)
      const resolvedMap = new Map<string, ResolvedSecurity>();
      for (const sec of resolved) {
        // If already have this ISIN, prefer NSE over BSE
        const existing = resolvedMap.get(sec.isin);
        if (!existing || (sec.exchange === 'NSE' && existing.exchange === 'BSE')) {
          resolvedMap.set(sec.isin, sec);
        }
      }

      // Filter positions to only include resolved ones
      const resolvedPositions: ResolvedParsedPosition[] = [];
      const unresolvedFromParsed: string[] = [];

      for (const position of parseResult.positions) {
        const resolvedSec = resolvedMap.get(position.isin);
        if (resolvedSec) {
          resolvedPositions.push({
            isin: resolvedSec.isin,
            ticker: position.symbol,  // Use original symbol from tradebook for display
            exchange: resolvedSec.exchange,
            bse_scrip_code: resolvedSec.bse_scrip_code,
            name: resolvedSec.name,
            tranches: position.tranches.map(t => ({
              ...t,
              isAlreadyImported: false, // Will be updated by check-duplicates API
            })),
          });
        } else {
          unresolvedFromParsed.push(position.isin);
        }
      }

      // Check for duplicate orderIds (against both open and closed positions)
      if (resolvedPositions.length > 0) {
        console.log('Checking for duplicate orderIds...');
        
        // Build request grouped by ISIN
        const tradesForDuplicateCheck = resolvedPositions.map(pos => ({
          isin: pos.isin,
          orderIds: pos.tranches.map(t => t.orderId),
        }));

        const duplicateCheckResponse = await checkDuplicateOrderIds(portfolioId, tradesForDuplicateCheck);
        
        if (duplicateCheckResponse.success && duplicateCheckResponse.data) {
          // Build a map for quick lookup: "ISIN:orderId" -> isAlreadyImported
          const duplicateMap = new Map<string, boolean>();
          for (const result of duplicateCheckResponse.data) {
            duplicateMap.set(`${result.isin}:${result.orderId}`, result.isAlreadyImported);
          }
          
          // Update tranches with isAlreadyImported flag
          for (const pos of resolvedPositions) {
            for (const tranche of pos.tranches) {
              const key = `${pos.isin}:${tranche.orderId}`;
              tranche.isAlreadyImported = duplicateMap.get(key) || false;
            }
          }
          
          // Log duplicate detection results
          const totalTranches = resolvedPositions.reduce((sum, p) => sum + p.tranches.length, 0);
          const duplicateTranches = resolvedPositions.reduce(
            (sum, p) => sum + p.tranches.filter(t => t.isAlreadyImported).length, 
            0
          );
          console.log(`Duplicate check complete: ${duplicateTranches} of ${totalTranches} tranches already imported`);
        } else {
          console.warn('Failed to check duplicates, proceeding without duplicate detection');
        }
      }

      // Combine unresolved from API and from parsed data
      const allUnresolvedIsins = Array.from(new Set([
        ...unresolved.isins,
        ...unresolvedFromParsed,
      ]));

      // Include unresolved symbols from generic parser (broker symbol lookup failures)
      const unresolvedSymbols = parseResult.unresolvedSymbols || [];

      // Set warnings if any
      if (parseResult.rejectedRows.length > 0 || allUnresolvedIsins.length > 0 || unresolvedSymbols.length > 0) {
        setImportWarnings({
          rejectedRows: parseResult.rejectedRows,
          unresolvedIsins: [...allUnresolvedIsins, ...unresolvedSymbols],
        });
      }

      if (resolvedPositions.length === 0) {
        throw new Error('No valid securities could be resolved. Please ensure broker symbol mappings and stock prices are uploaded.');
      }

      setParsedData(resolvedPositions);
      setStep('existing-positions');
    } catch (error) {
      console.error('Error parsing file:', error);
      setUploadError(error instanceof Error ? error.message : 'Failed to parse file');
      setSelectedBroker(null);
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleExistingPositionsContinue = (existingData: any[]) => {
    setExistingPositionsData(existingData);
    setStep('new-positions');
  };

  const handleImportComplete = () => {
    onImportComplete();
    handleClose();
  };

  const handleClose = () => {
    setStep('broker-selection');
    setSelectedBroker(null);
    setParsedData([]);
    setUploadError(null);
    setImportWarnings(null);
    onClose();
  };

  // Warning Banner Component
  const WarningBanner = () => {
    if (!importWarnings) return null;
    
    const { rejectedRows, unresolvedIsins } = importWarnings;
    const hasRejectedRows = rejectedRows.length > 0;
    const hasUnresolved = unresolvedIsins.length > 0;
    
    if (!hasRejectedRows && !hasUnresolved) return null;
    
    return (
      <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-amber-800 mb-2">Some rows could not be imported</p>
            
            {hasRejectedRows && (
              <div className="mb-3">
                <p className="text-sm text-amber-700 mb-1">
                  Rejected rows (missing ISIN):
                </p>
                <ul className="text-xs text-amber-700 list-disc list-inside max-h-20 overflow-y-auto">
                  {rejectedRows.slice(0, 5).map((row, idx) => (
                    <li key={idx}>
                      Row {row.rowNumber}: {row.symbol || 'Unknown'} - {row.reason}
                    </li>
                  ))}
                  {rejectedRows.length > 5 && (
                    <li className="text-amber-600">...and {rejectedRows.length - 5} more</li>
                  )}
                </ul>
              </div>
            )}
            
            {hasUnresolved && (
              <div>
                <p className="text-sm text-amber-700 mb-1">
                  Unresolved securities (not in price database):
                </p>
                <ul className="text-xs text-amber-700 list-disc list-inside max-h-20 overflow-y-auto">
                  {unresolvedIsins.slice(0, 5).map((isin, idx) => (
                    <li key={idx}>ISIN: {isin}</li>
                  ))}
                  {unresolvedIsins.length > 5 && (
                    <li className="text-amber-600">...and {unresolvedIsins.length - 5} more</li>
                  )}
                </ul>
              </div>
            )}
            
            <p className="text-xs text-amber-600 mt-2">
              Please contact support if you need help with these securities.
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Data</DialogTitle>
          </DialogHeader>

          {/* Step 1: Broker Selection */}
          {step === 'broker-selection' && (
            <div className="py-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Select your broker to upload tradebook
              </h3>
              
              {uploadError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{uploadError}</p>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {portfolioBrokers.map((broker) => {
                  const info = brokerInfo[broker];
                  if (!info) return null;

                  return (
                    <Card
                      key={broker}
                      className={`p-6 cursor-pointer transition-all duration-200 ${
                        info.enabled
                          ? 'hover:border-emerald-500 hover:shadow-md'
                          : 'opacity-50 cursor-not-allowed'
                      }`}
                      onClick={() => handleBrokerSelect(broker)}
                    >
                      <div className="flex flex-col items-center gap-3">
                        <div className="text-4xl">{info.logo}</div>
                        <p className="font-medium text-gray-900">{info.name}</p>
                        {info.enabled ? (
                          <div className="flex flex-col items-center gap-1">
                            <div className="flex items-center gap-1 text-xs text-emerald-600">
                              <Check className="w-3 h-3" />
                              <span>Available</span>
                            </div>
                            <span className="text-xs text-gray-400">{info.fileType} file</span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">Coming Soon</span>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>

              {/* Help text for adding more brokers */}
              <p className="mt-4 text-sm text-gray-500 text-center">
                Want to import from a different broker? Update your brokers list in the portfolio settings.
              </p>

              {isUploading && (
                <div className="mt-6 text-center">
                  <div className="inline-flex items-center gap-2 text-gray-600">
                    <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                    <span>Processing {selectedBroker === 'Groww' ? 'Excel' : 'CSV'} file...</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Existing Positions */}
          {step === 'existing-positions' && (
            <>
              <WarningBanner />
              <ExistingPositionsScreen
                parsedData={parsedData}
                portfolioId={portfolioId}
                onContinue={handleExistingPositionsContinue}
              />
            </>
          )}

          {/* Step 3: New Positions */}
          {step === 'new-positions' && selectedBroker && (
            <>
              <WarningBanner />
              <NewPositionsScreen
                parsedData={parsedData}
                portfolioId={portfolioId}
                broker={brokerNameToEnum[selectedBroker] || Broker.ZERODHA}
                existingPositionsData={existingPositionsData}
                onComplete={handleImportComplete}
                onBack={() => setStep('existing-positions')}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Hidden file input - accept based on selected broker */}
      <input
        ref={fileInputRef}
        type="file"
        accept={
          selectedBroker === 'Groww' || selectedBroker === 'ICICI Direct' 
            ? '.xlsx,.xls,.csv' 
            : '.csv'
        }
        className="hidden"
        onChange={handleFileUpload}
      />
    </>
  );
};

export default ImportDataModal;
